/**
 * Two-Factor Authentication Setup Component (Story 1.7)
 * Multi-step wizard for enabling 2FA.
 */
import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useTwoFactorStore } from "../../store/twoFactorStore";
import { Modal } from "../molecules/Modal";
import { Button } from "../atoms/Button";
import { Input } from "../atoms/Input";
import { BackupCodesDisplay } from "../molecules/BackupCodesDisplay";
import {
  DevicePhoneMobileIcon,
  QrCodeIcon,
  ShieldCheckIcon,
} from "@heroicons/react/24/outline";

interface TwoFactorSetupProps {
  isOpen: boolean;
  onClose: () => void;
  onComplete?: () => void;
}

type SetupStep = "qr-code" | "verify" | "backup-codes";

export function TwoFactorSetup({ isOpen, onClose, onComplete }: TwoFactorSetupProps) {
  const { t } = useTranslation("settings");
  const [step, setStep] = useState<SetupStep>("qr-code");
  const [code, setCode] = useState("");
  const [verifyError, setVerifyError] = useState<string | null>(null);

  const {
    setupData,
    backupCodes,
    loading,
    error,
    startSetup,
    verifyAndEnable,
    clearSetupData,
    clearBackupCodes,
    clearError,
  } = useTwoFactorStore();

  // Start setup when modal opens
  useEffect(() => {
    if (isOpen && !setupData) {
      startSetup().catch(() => {});
    }
  }, [isOpen, setupData, startSetup]);

  // Reset state when modal closes
  const handleClose = () => {
    setStep("qr-code");
    setCode("");
    setVerifyError(null);
    clearSetupData();
    clearBackupCodes();
    clearError();
    onClose();
  };

  const handleVerify = async () => {
    if (code.length !== 6) {
      setVerifyError(t("setup_verify_code_required"));
      return;
    }

    setVerifyError(null);
    try {
      await verifyAndEnable(code);
      setStep("backup-codes");
    } catch (err) {
      setVerifyError(err instanceof Error ? err.message : t("setup_verify_failed"));
    }
  };

  const handleComplete = () => {
    handleClose();
    onComplete?.();
  };

  const renderStepIndicator = () => (
    <div className="flex items-center justify-center gap-2 mb-6">
      {[
        { key: "qr-code", icon: QrCodeIcon },
        { key: "verify", icon: DevicePhoneMobileIcon },
        { key: "backup-codes", icon: ShieldCheckIcon },
      ].map(({ key, icon: Icon }, index) => (
        <div key={key} className="flex items-center">
          <div
            className={`
              w-8 h-8 rounded-full flex items-center justify-center
              ${
                step === key
                  ? "bg-primary-500 text-white"
                  : index < ["qr-code", "verify", "backup-codes"].indexOf(step)
                    ? "bg-success-500 text-white"
                    : "bg-neutral-200 dark:bg-neutral-700 text-neutral-500"
              }
            `}
          >
            <Icon className="w-4 h-4" />
          </div>
          {index < 2 && (
            <div
              className={`w-8 h-0.5 ${
                index < ["qr-code", "verify", "backup-codes"].indexOf(step)
                  ? "bg-success-500"
                  : "bg-neutral-200 dark:bg-neutral-700"
              }`}
            />
          )}
        </div>
      ))}
    </div>
  );

  const renderQRCodeStep = () => (
    <div className="space-y-4">
      <p className="text-sm text-neutral-600 dark:text-neutral-400">
        {t("setup_scan_qr")}
      </p>

      {loading && !setupData && (
        <div className="flex justify-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-500" />
        </div>
      )}

      {error && (
        <div className="p-3 bg-danger-50 dark:bg-danger-900/20 text-danger-700 dark:text-danger-300 rounded-lg text-sm">
          {error}
        </div>
      )}

      {setupData && (
        <>
          {/* QR Code */}
          <div className="flex justify-center p-4 bg-white rounded-lg">
            <img
              src={`data:image/png;base64,${setupData.qrCode}`}
              alt={t("setup_qr_alt")}
              className="w-48 h-48"
            />
          </div>

          {/* Manual key */}
          <div className="p-3 bg-neutral-50 dark:bg-neutral-900 rounded-lg">
            <p className="text-xs text-neutral-500 dark:text-neutral-400 mb-1">
              {t("setup_manual_key_label")}
            </p>
            <p className="font-mono text-sm break-all text-neutral-900 dark:text-neutral-100">
              {setupData.manualKey}
            </p>
          </div>

          <Button variant="primary" onClick={() => setStep("verify")} className="w-full">
            {t("setup_btn_continue")}
          </Button>
        </>
      )}
    </div>
  );

  const renderVerifyStep = () => (
    <div className="space-y-4">
      <p className="text-sm text-neutral-600 dark:text-neutral-400">
        {t("setup_verify_prompt")}
      </p>

      <Input
        label={t("setup_verify_code_label")}
        type="text"
        inputMode="numeric"
        maxLength={6}
        value={code}
        onChange={(e) => {
          const val = e.target.value.replace(/\D/g, "").slice(0, 6);
          setCode(val);
          setVerifyError(null);
        }}
        error={verifyError || undefined}
        placeholder={t("setup_verify_code_placeholder")}
        autoFocus
        className="text-center text-2xl tracking-widest font-mono"
      />

      <div className="flex gap-2">
        <Button variant="ghost" onClick={() => setStep("qr-code")} className="flex-1">
          {t("setup_btn_back")}
        </Button>
        <Button
          variant="primary"
          onClick={handleVerify}
          loading={loading}
          disabled={code.length !== 6}
          className="flex-1"
        >
          {t("setup_btn_verify")}
        </Button>
      </div>
    </div>
  );

  const renderBackupCodesStep = () => (
    <div className="space-y-4">
      <div className="text-center">
        <ShieldCheckIcon className="w-12 h-12 mx-auto text-success-500 mb-2" />
        <h3 className="text-lg font-semibold text-neutral-900 dark:text-neutral-100">
          {t("setup_success_title")}
        </h3>
      </div>

      <BackupCodesDisplay codes={backupCodes} onConfirm={handleComplete} />
    </div>
  );

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title={t("setup_modal_title")}
      size="md"
    >
      {renderStepIndicator()}

      {step === "qr-code" && renderQRCodeStep()}
      {step === "verify" && renderVerifyStep()}
      {step === "backup-codes" && renderBackupCodesStep()}
    </Modal>
  );
}
